/* ============================================================
   LIVVEWELL — CRO CART DRAWER CONTROLLER
   ============================================================
   File:
   assets/lw-cart-drawer.js

   Handles:
   - Drawer open / close
   - Focus trapping
   - Body scroll lock
   - Cart link interception
   - External AJAX Add to Cart detection
   - Quantity changes
   - Remove item
   - Bundled section re-rendering
   - Sale countdown
   - Discount-code application
   - Smart product recommendations
   - Complementary -> related -> merchant fallback
   - Cart-aware upsell ranking
   - Variant selection
   - Selling plan selection
   - Upsell Add to Cart
   - Live cart count updates
   ============================================================ */

(function () {
  'use strict';


  /* ==========================================================
     PREVENT DOUBLE INITIALIZATION
  ========================================================== */

  if (
    window.LivvewellCartDrawer &&
    window.LivvewellCartDrawer.__loaded
  ) {
    return;
  }



  /* ==========================================================
     GLOBALS
  ========================================================== */

  const ROOT_SELECTOR =
    '[data-lw-cart-drawer]';


  const NativeFetch =
    window.fetch.bind(window);


  const recommendationCache =
    new Map();


  const productCache =
    new Map();


  let controller = null;



  /* ==========================================================
     SHOPIFY ROOT
  ========================================================== */

  function shopRoot() {

    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {

      return window.Shopify.routes.root;

    }

    return '/';

  }



  /* ==========================================================
     ENDPOINT
  ========================================================== */

  function endpoint(path) {

    return (
      shopRoot() +
      String(path).replace(/^\/+/, '')
    );

  }



  /* ==========================================================
     NORMALIZE CART MUTATION URL
  ========================================================== */

  function getCartMutationType(url) {

    try {

      const parsed =
        new URL(
          url,
          window.location.origin
        );


      const match =
        parsed.pathname.match(
          /\/cart\/(add|change|update|clear)(?:\.js)?$/
        );


      return match
        ? match[1]
        : null;

    } catch (error) {

      return null;

    }

  }



  /* ==========================================================
     GET REQUEST URL
  ========================================================== */

  function requestUrl(input) {

    if (
      typeof input === 'string'
    ) {
      return input;
    }


    if (
      input instanceof URL
    ) {
      return input.href;
    }


    if (
      input &&
      typeof input.url === 'string'
    ) {
      return input.url;
    }


    return '';

  }



  /* ==========================================================
     GET REQUEST METHOD
  ========================================================== */

  function requestMethod(
    input,
    init
  ) {

    if (
      init &&
      init.method
    ) {

      return init.method
        .toUpperCase();

    }


    if (
      input &&
      input.method
    ) {

      return input.method
        .toUpperCase();

    }


    return 'GET';

  }



  /* ==========================================================
     ESCAPE HTML
  ========================================================== */

  function escapeHTML(value) {

    return String(
      value == null
        ? ''
        : value
    )
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  }



  /* ==========================================================
     ESCAPE ATTRIBUTE
  ========================================================== */

  function escapeAttr(value) {

    return escapeHTML(value);

  }



  /* ==========================================================
     NUMBER
  ========================================================== */

  function toNumber(
    value,
    fallback = 0
  ) {

    const parsed =
      Number(value);


    return Number.isFinite(parsed)
      ? parsed
      : fallback;

  }



  /* ==========================================================
     PRODUCT HANDLE FROM URL
  ========================================================== */

  function handleFromUrl(url) {

    if (!url) {
      return '';
    }


    try {

      const parsed =
        new URL(
          url,
          window.location.origin
        );


      const match =
        parsed.pathname.match(
          /\/products\/([^/?#]+)/
        );


      return match
        ? decodeURIComponent(match[1])
        : '';

    } catch (error) {

      return '';

    }

  }



  /* ==========================================================
     FORMAT MONEY
  ========================================================== */

  function formatMoney(
    cents,
    currency
  ) {

    const amount =
      toNumber(cents) / 100;


    try {

      return new Intl.NumberFormat(
        document.documentElement.lang ||
        'en',
        {
          style: 'currency',
          currency:
            currency || 'USD'
        }
      ).format(amount);

    } catch (error) {

      return '$' +
        amount.toFixed(2);

    }

  }

  /* ==========================================================
     GET PRODUCT VARIANT IMAGE
  ========================================================== */
function getVariantImage(variant, product) {

  if (!variant) {
    return getProductImage(product);
  }

  const featured =
    variant.featured_image;

  if (typeof featured === 'string') {
    return featured;
  }

  if (featured) {
    if (featured.src) {
      return featured.src;
    }

    if (featured.url) {
      return featured.url;
    }
  }

  if (
    variant.featured_media &&
    variant.featured_media.preview_image
  ) {

    return (
      variant.featured_media.preview_image.src ||
      variant.featured_media.preview_image.url ||
      ''
    );

  }

  return getProductImage(product);

}


  /* ==========================================================
     GET PRODUCT IMAGE
  ========================================================== */

  function getProductImage(product) {

    if (!product) {
      return '';
    }


    const featured =
      product.featured_image;


    if (
      typeof featured === 'string'
    ) {
      return featured;
    }


    if (featured) {

      if (featured.url) {
        return featured.url;
      }

      if (featured.src) {
        return featured.src;
      }

    }


    if (
      Array.isArray(product.images) &&
      product.images.length
    ) {

      const first =
        product.images[0];


      if (
        typeof first === 'string'
      ) {
        return first;
      }


      if (first) {
        return (
          first.url ||
          first.src ||
          ''
        );
      }

    }


    return '';

  }



  /* ==========================================================
     CART PATH CHECK
  ========================================================== */

  function isCartPageUrl(url) {

    try {

      const parsed =
        new URL(
          url,
          window.location.origin
        );


      let root =
        shopRoot();


      if (!root.startsWith('/')) {
        root = '/';
      }


      const expected =
        (
          root +
          'cart'
        )
          .replace(/\/+/g, '/')
          .replace(/\/$/, '');


      const actual =
        parsed.pathname
          .replace(/\/+/g, '/')
          .replace(/\/$/, '');


      return actual === expected;

    } catch (error) {

      return false;

    }

  }



  /* ==========================================================
     GET CART
  ========================================================== */

  async function getCart() {

    const response =
      await NativeFetch(
        endpoint('cart.js'),
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );


    if (!response.ok) {

      throw new Error(
        'Unable to load the cart.'
      );

    }


    return response.json();

  }



  /* ==========================================================
     NETWORK OBSERVER
     ----------------------------------------------------------
     We observe the existing theme's Ajax cart requests rather
     than forcing every product form through our own system.
  ========================================================== */

  function installFetchObserver() {

    if (
      window.__lwCartFetchObserver
    ) {
      return;
    }


    window.__lwCartFetchObserver =
      true;


    window.fetch =
      async function () {

        const args =
          Array.from(arguments);


        const input =
          args[0];


        const init =
          args[1] || {};


        const url =
          requestUrl(input);


        const method =
          requestMethod(
            input,
            init
          );


        const mutationType =
          method === 'POST'
            ? getCartMutationType(url)
            : null;


        const response =
          await NativeFetch.apply(
            window,
            args
          );


        if (
          mutationType &&
          response.ok
        ) {

          const clone =
            response.clone();


          clone
            .json()
            .then(function (payload) {

              window.dispatchEvent(
                new CustomEvent(
                  'lw:external-cart-mutation',
                  {
                    detail: {
                      type:
                        mutationType,

                      payload:
                        payload || {}
                    }
                  }
                )
              );

            })
            .catch(function () {

              window.dispatchEvent(
                new CustomEvent(
                  'lw:external-cart-mutation',
                  {
                    detail: {
                      type:
                        mutationType,

                      payload: {}
                    }
                  }
                )
              );

            });

        }


        return response;

      };

  }



  /* ==========================================================
     XHR OBSERVER
     ----------------------------------------------------------
     Some themes still use XMLHttpRequest instead of fetch.
  ========================================================== */

  function installXHRObserver() {

    if (
      window.__lwCartXHRObserver
    ) {
      return;
    }


    window.__lwCartXHRObserver =
      true;


    const OriginalOpen =
      XMLHttpRequest.prototype.open;


    const OriginalSend =
      XMLHttpRequest.prototype.send;


    XMLHttpRequest.prototype.open =
      function (
        method,
        url
      ) {

        this.__lwMethod =
          String(method || 'GET')
            .toUpperCase();


        this.__lwUrl =
          url;


        return OriginalOpen.apply(
          this,
          arguments
        );

      };


    XMLHttpRequest.prototype.send =
      function () {

        const xhr =
          this;


        const mutationType =
          xhr.__lwMethod === 'POST'
            ? getCartMutationType(
                xhr.__lwUrl
              )
            : null;


        if (mutationType) {

          xhr.addEventListener(
            'loadend',
            function () {

              if (
                xhr.status < 200 ||
                xhr.status >= 300
              ) {
                return;
              }


              let payload = {};


              try {

                payload =
                  JSON.parse(
                    xhr.responseText
                  );

              } catch (error) {}


              window.dispatchEvent(
                new CustomEvent(
                  'lw:external-cart-mutation',
                  {
                    detail: {
                      type:
                        mutationType,

                      payload:
                        payload || {}
                    }
                  }
                )
              );

            },
            {
              once: true
            }
          );

        }


        return OriginalSend.apply(
          this,
          arguments
        );

      };

  }



  /* ==========================================================
     DRAWER CONTROLLER
  ========================================================== */

  class LivvewellDrawer {

    constructor(root) {

      this.root =
        root;


      this.previousFocus =
        null;


      this.pageScrollY =
        0;


      this.bodyStyle =
        null;


      this.timerInterval =
        null;


      this.toastTimer =
        null;


      this.externalRefreshTimer =
        null;


      this.mutationQueue =
        Promise.resolve();


      this.lastAddedProductId =
        null;


      this.upsellRequestId =
        0;


      this.upsellProducts =
        new Map();


      this.globalEventsBound =
        false;


      this.parseConfig();

      this.bindRoot();

      this.bindGlobalEvents();

      this.startSaleTimer();

      this.updateRewardThreshold();
    }



    /* ========================================================
       CONFIG
    ======================================================== */

    parseConfig() {

      const configElement =
        this.root.querySelector(
          '[data-lw-cart-config]'
        );


      this.config = {};


      if (!configElement) {
        return;
      }


      try {

        this.config =
          JSON.parse(
            configElement.textContent
          );

      } catch (error) {

        console.warn(
          '[Livvewell Cart] Invalid configuration JSON.',
          error
        );

        this.config = {};

      }


      this.sectionId =
        String(
          this.config.sectionId ||
          this.root.dataset.sectionId ||
          ''
        );

    }





/* ========================================================
   UPDATE REWARD THRESHOLD FOR ACTIVE CURRENCY
======================================================== */

updateRewardThreshold() {

  const reward =
    this.root.querySelector(
      '[data-lw-reward]'
    );


  if (!reward) {
    return;
  }


  /*
    Only threshold mode needs currency conversion.
  */

  if (
    !reward.classList.contains(
      'lw-cd-reward--threshold'
    )
  ) {
    return;
  }


  const baseThresholdCents =
    toNumber(
      reward.dataset.thresholdBaseCents
    );


  const cartTotalCents =
    toNumber(
      reward.dataset.cartTotalCents
    );


  const cartCurrency =
    reward.dataset.cartCurrency ||
    this.config.currency ||
    'USD';


  const shopCurrency =
    reward.dataset.shopCurrency ||
    'USD';


  const label =
    reward.dataset.rewardLabel ||
    'your reward';


  const successText =
    reward.dataset.rewardSuccess ||
    'Reward unlocked';


  if (baseThresholdCents <= 0) {
    return;
  }


  /*
    Theme Editor threshold is entered in the
    STORE currency.

    Example:
    USD 75 = 7500 cents.

    cart.total_price, however, is already in the
    customer's presentment currency.
  */

  let currencyRate = 1;


  if (
    cartCurrency !== shopCurrency
  ) {

    if (
      window.Shopify &&
      window.Shopify.currency &&
      Number.isFinite(
        Number(
          window.Shopify.currency.rate
        )
      ) &&
      Number(
        window.Shopify.currency.rate
      ) > 0
    ) {

      currencyRate =
        Number(
          window.Shopify.currency.rate
        );

    } else {

      /*
        Do NOT show a potentially incorrect
        threshold if Shopify hasn't supplied
        the exchange rate.
      */

      reward.style.display =
        'none';

      return;

    }

  }


  const convertedThresholdCents =
    Math.round(
      baseThresholdCents *
      currencyRate
    );


  const remainingCents =
    Math.max(
      0,
      convertedThresholdCents -
      cartTotalCents
    );


  const percent =
    Math.min(
      100,
      Math.max(
        0,
        convertedThresholdCents > 0
          ? (
              cartTotalCents /
              convertedThresholdCents
            ) * 100
          : 0
      )
    );


  const copy =
    reward.querySelector(
      '.lw-cd-reward__copy'
    );


  const track =
    reward.querySelector(
      '.lw-cd-reward__track'
    );


  const fill =
    track
      ? track.querySelector('span')
      : null;


  /*
    Update progress bar.
  */

  if (track) {

    track.setAttribute(
      'aria-valuenow',
      String(
        Math.round(percent)
      )
    );

  }


  if (fill) {

    fill.style.width =
      percent + '%';

  }


  /*
    Update copy.
  */

  if (copy) {

    if (
      cartTotalCents >=
      convertedThresholdCents
    ) {

      copy.innerHTML =
        `
          <div class="lw-cd-reward__success">

            <span class="lw-cd-reward__check">

              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8.4l3 3L13 4.8"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

            </span>

            <strong>
              ${escapeHTML(successText)}
            </strong>

          </div>
        `;

    } else {

      copy.innerHTML =
        `
          <p>
            You're
            <strong>
              ${escapeHTML(
                formatMoney(
                  remainingCents,
                  cartCurrency
                )
              )}
            </strong>
            away from
            <strong>
              ${escapeHTML(label)}
            </strong>.
          </p>
        `;

    }

  }


  reward.style.display =
    '';

}









    /* ========================================================
       GET REFERENCES
    ======================================================== */

    refs() {

      return {

        dialog:
          this.root.querySelector(
            '[data-lw-cart-dialog]'
          ),

        scroll:
          this.root.querySelector(
            '[data-lw-cart-scroll]'
          ),

        toast:
          this.root.querySelector(
            '[data-lw-cart-toast]'
          ),

        upsells:
          this.root.querySelector(
            '[data-lw-upsells]'
          ),

        upsellList:
          this.root.querySelector(
            '[data-lw-upsell-list]'
          ),

        upsellLoading:
          this.root.querySelector(
            '[data-lw-upsell-loading]'
          )

      };

    }



    /* ========================================================
       IS OPEN
    ======================================================== */

    isOpen() {

      return (
        this.root.classList.contains(
          'is-open'
        ) ||
        this.root.getAttribute(
          'aria-hidden'
        ) === 'false'
      );

    }



    /* ========================================================
       OPEN
    ======================================================== */

    open(options = {}) {

      if (this.isOpen()) {
        return;
      }


      this.previousFocus =
        options.trigger ||
        document.activeElement;


      this.lockPage();


      this.root.classList.add(
        'is-open'
      );


      this.root.setAttribute(
        'aria-hidden',
        'false'
      );


      const {
        dialog
      } = this.refs();


      window.requestAnimationFrame(
        function () {

          window.setTimeout(
            function () {

              const close =
                this.root.querySelector(
                  '[data-lw-cart-close]'
                );


              if (close) {

                close.focus({
                  preventScroll: true
                });

              } else if (dialog) {

                dialog.focus({
                  preventScroll: true
                });

              }

            }.bind(this),
            80
          );

        }.bind(this)
      );


      this.startSaleTimer();


      if (
        this.config.upsells &&
        this.config.upsells.enabled
      ) {

        this.loadUpsells();

      }


      document.dispatchEvent(
        new CustomEvent(
          'lw:cart:open'
        )
      );

    }



    /* ========================================================
       CLOSE
    ======================================================== */

    close(options = {}) {

      if (!this.isOpen()) {
        return;
      }


      this.root.classList.remove(
        'is-open'
      );


      this.root.setAttribute(
        'aria-hidden',
        'true'
      );


      this.unlockPage();


      if (
        options.restoreFocus !== false &&
        this.previousFocus &&
        document.contains(
          this.previousFocus
        )
      ) {

        window.setTimeout(
          function () {

            try {

              this.previousFocus.focus({
                preventScroll: true
              });

            } catch (error) {}

          }.bind(this),
          80
        );

      }


      document.dispatchEvent(
        new CustomEvent(
          'lw:cart:close'
        )
      );

    }



    /* ========================================================
       PAGE SCROLL LOCK
    ======================================================== */

    lockPage() {

      if (this.bodyStyle) {
        return;
      }


      this.pageScrollY =
        window.scrollY;


      this.bodyStyle = {

        position:
          document.body.style.position,

        top:
          document.body.style.top,

        left:
          document.body.style.left,

        right:
          document.body.style.right,

        width:
          document.body.style.width

      };


      document.documentElement
        .classList.add(
          'lw-cart-drawer-open'
        );


      document.body
        .classList.add(
          'lw-cart-drawer-open'
        );


      document.body.style.position =
        'fixed';


      document.body.style.top =
        '-' +
        this.pageScrollY +
        'px';


      document.body.style.left =
        '0';


      document.body.style.right =
        '0';


      document.body.style.width =
        '100%';

    }



    /* ========================================================
       PAGE SCROLL UNLOCK
    ======================================================== */

    unlockPage() {

      if (!this.bodyStyle) {
        return;
      }


      document.documentElement
        .classList.remove(
          'lw-cart-drawer-open'
        );


      document.body
        .classList.remove(
          'lw-cart-drawer-open'
        );


      document.body.style.position =
        this.bodyStyle.position;


      document.body.style.top =
        this.bodyStyle.top;


      document.body.style.left =
        this.bodyStyle.left;


      document.body.style.right =
        this.bodyStyle.right;


      document.body.style.width =
        this.bodyStyle.width;


      this.bodyStyle =
        null;


      window.scrollTo(
        0,
        this.pageScrollY
      );

    }



    /* ========================================================
       TOAST
    ======================================================== */

    toast(
      message,
      isError = false
    ) {

      const {
        toast
      } = this.refs();


      if (!toast) {
        return;
      }


      window.clearTimeout(
        this.toastTimer
      );


      toast.textContent =
        message;


      toast.classList.toggle(
        'is-error',
        !!isError
      );


      toast.classList.add(
        'is-visible'
      );


      this.toastTimer =
        window.setTimeout(
          function () {

            toast.classList.remove(
              'is-visible'
            );

          },
          2600
        );

    }



    /* ========================================================
       BUSY
    ======================================================== */

    setBusy(value) {

      this.root.classList.toggle(
        'is-busy',
        !!value
      );

    }



    /* ========================================================
       ROOT EVENTS
    ======================================================== */

    bindRoot() {

      if (
        this.root.dataset.lwCartBound ===
        'true'
      ) {
        return;
      }


      this.root.dataset.lwCartBound =
        'true';


      this.root.addEventListener(
        'click',
        this.handleRootClick.bind(this)
      );


      this.root.addEventListener(
        'change',
        this.handleRootChange.bind(this)
      );

    }



    /* ========================================================
       ROOT CLICK
    ======================================================== */

    handleRootClick(event) {

      const close =
        event.target.closest(
          '[data-lw-cart-close]'
        );


      if (close) {

        event.preventDefault();

        this.close();

        return;

      }



      /* QUANTITY MINUS */

      const minus =
        event.target.closest(
          '[data-lw-qty-minus]'
        );


      if (minus) {

        event.preventDefault();


        const line =
          minus.closest(
            '[data-lw-cart-line]'
          );


        const input =
          line &&
          line.querySelector(
            '[data-lw-quantity]'
          );


        if (
          !line ||
          !input
        ) {
          return;
        }


        const current =
          Math.max(
            0,
            parseInt(
              input.value,
              10
            ) || 0
          );


        this.changeLine(
          line,
          Math.max(
            0,
            current - 1
          )
        );


        return;

      }



      /* QUANTITY PLUS */

      const plus =
        event.target.closest(
          '[data-lw-qty-plus]'
        );


      if (plus) {

        event.preventDefault();


        const line =
          plus.closest(
            '[data-lw-cart-line]'
          );


        const input =
          line &&
          line.querySelector(
            '[data-lw-quantity]'
          );


        if (
          !line ||
          !input
        ) {
          return;
        }


        const current =
          Math.max(
            0,
            parseInt(
              input.value,
              10
            ) || 0
          );


        this.changeLine(
          line,
          current + 1
        );


        return;

      }



      /* REMOVE */

      const remove =
        event.target.closest(
          '[data-lw-cart-remove]'
        );


      if (remove) {

        event.preventDefault();


        const line =
          remove.closest(
            '[data-lw-cart-line]'
          );


        if (line) {

          this.changeLine(
            line,
            0
          );

        }


        return;

      }



      /* PROMO CODE */

      const promoCode =
        event.target.closest(
          '[data-lw-copy-promo]'
        );


      if (promoCode) {

        event.preventDefault();


        const code =
          promoCode.dataset.code ||
          '';


        if (code) {

          this.copyText(
            code
          );


          const discount =
            this.root.querySelector(
              '[data-lw-discount]'
            );


          const input =
            this.root.querySelector(
              '[data-lw-discount-input]'
            );


          if (discount) {
            discount.open = true;
          }


          if (input) {

            input.value =
              code;

          }


          this.toast(
            'Discount code copied.'
          );

        }


        return;

      }



      /* APPLY DISCOUNT */

      const applyDiscount =
        event.target.closest(
          '[data-lw-discount-apply]'
        );


      if (applyDiscount) {

        event.preventDefault();

        this.applyDiscount();

        return;

      }



      /* UPSELL OPTIONS */

      const choose =
        event.target.closest(
          '[data-lw-upsell-toggle]'
        );


      if (choose) {

        event.preventDefault();

        this.toggleUpsellOptions(
          choose
        );

        return;

      }



      /* DIRECT UPSELL ADD */

      const directAdd =
        event.target.closest(
          '[data-lw-upsell-direct-add]'
        );


      if (directAdd) {

        event.preventDefault();

        this.addDirectUpsell(
          directAdd
        );

        return;

      }



      /* CONFIRM UPSELL */

      const confirmUpsell =
        event.target.closest(
          '[data-lw-upsell-confirm]'
        );


      if (confirmUpsell) {

        event.preventDefault();

        this.confirmUpsell(
          confirmUpsell
        );

      }

    }



    /* ========================================================
       ROOT CHANGE
    ======================================================== */

    handleRootChange(event) {

      /* CART QUANTITY */

      if (
        event.target.matches(
          '[data-lw-quantity]'
        )
      ) {

        const input =
          event.target;


        const line =
          input.closest(
            '[data-lw-cart-line]'
          );


        let quantity =
          parseInt(
            input.value,
            10
          );


        if (
          !Number.isFinite(quantity) ||
          quantity < 0
        ) {

          quantity = 1;

          input.value = 1;

        }


        if (line) {

          this.changeLine(
            line,
            quantity
          );

        }


        return;

      }



      /* UPSELL VARIANT */

      if (
        event.target.matches(
          '[data-lw-upsell-variant]'
        )
      ) {

        const card =
          event.target.closest(
            '[data-lw-upsell]'
          );


        if (card) {

          this.updateUpsellPlanOptions(
            card
          );

          this.updateUpsellPrice(
            card
          );

          this.updateUpsellImage(
    card
  );

        }


        return;

      }



      /* UPSELL SELLING PLAN */

      if (
        event.target.matches(
          '[data-lw-upsell-plan]'
        )
      ) {

        const card =
          event.target.closest(
            '[data-lw-upsell]'
          );


        if (card) {

          this.updateUpsellPrice(
            card
          );

        }

      }

    }



    /* ========================================================
       GLOBAL EVENTS
    ======================================================== */

    bindGlobalEvents() {

      if (this.globalEventsBound) {
        return;
      }


      this.globalEventsBound =
        true;



      /* CART OPEN / CART LINKS */

      /* ========================================================
   CART OPEN / CART LINKS

   IMPORTANT:
   Capture phase is intentional.

   The old theme drawer also listens for the header cart
   trigger. We intercept the click BEFORE its listener gets
   the opportunity to open the legacy drawer.
======================================================== */

document.addEventListener(
  'click',
  function (event) {

    const target =
      event.target instanceof Element
        ? event.target
        : event.target.parentElement;


    if (!target) {
      return;
    }



    /*
      Never interfere with links/buttons inside
      our own cart drawer.
    */

    if (
      target.closest(
        ROOT_SELECTOR
      )
    ) {
      return;
    }



    /* ------------------------------------------------------
       EXPLICIT NEW DRAWER TRIGGER
    ------------------------------------------------------ */

    let trigger =
      target.closest(
        '[data-lw-cart-open]'
      );



    /* ------------------------------------------------------
       NORMAL /CART LINKS
    ------------------------------------------------------ */

    if (
      !trigger &&
      this.config.interceptCartLinks
    ) {

      const link =
        target.closest(
          'a[href]'
        );


      if (
        link &&
        !link.hasAttribute(
          'data-lw-cart-bypass'
        )
      ) {

        /*
          Preserve normal browser behaviour for
          Cmd/Ctrl click, new tabs, etc.
        */

        const modifiedClick =
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey;


        if (
          !modifiedClick &&
          isCartPageUrl(
            link.href
          )
        ) {

          trigger = link;

        }

      }

    }



    /* ------------------------------------------------------
       NOT A CART TRIGGER
    ------------------------------------------------------ */

    if (!trigger) {
      return;
    }



    /*
      This is the important part.

      Prevent:
      1. normal /cart navigation
      2. old drawer listener
      3. other legacy cart click handlers
    */

    event.preventDefault();

    event.stopPropagation();

    event.stopImmediatePropagation();



    /*
      Refresh first so the drawer always opens with
      the latest Shopify cart state.
    */

    this.refresh({
      openAfter: true,
      trigger: trigger
    });

  }.bind(this),

  /*
    TRUE = CAPTURE PHASE.

    This makes our listener run before the old
    theme's normal click listeners.
  */

  true
);




/* ========================================================
   LIVVEWELL PRODUCT CARD ADD TO CART

   The shared lw-product-card snippet uses:

   form.lw-card__form

   These forms normally POST to Shopify and redirect to
   /cart. We intercept only these Livvewell product cards
   and submit them through Shopify's Ajax Cart API.
======================================================== */

document.addEventListener(
  'submit',
  function (event) {

    const form =
      event.target;


    if (
      !form ||
      !form.matches ||
      !form.matches(
        'form.lw-card__form'
      )
    ) {
      return;
    }


    /*
      Stop the browser's native Shopify product-form
      submission and any legacy theme cart handlers.
    */

    event.preventDefault();

    event.stopPropagation();

    event.stopImmediatePropagation();


    const submitter =
      event.submitter ||
      form.querySelector(
        'button[type="submit"]'
      );


    this.submitProductCard(
      form,
      submitter
    );

  }.bind(this),

  /*
    Capture phase means we grab our custom card forms
    before an older theme script can process them.
  */

  true
);




      /* KEYBOARD */

      document.addEventListener(
        'keydown',
        this.handleKeydown.bind(this)
      );



      /* EXISTING THEME CART MUTATIONS */

      window.addEventListener(
        'lw:external-cart-mutation',
        this.handleExternalMutation.bind(
          this
        )
      );



      /* RETURN FROM BF CACHE */

      window.addEventListener(
        'pageshow',
        function (event) {

          if (
            event.persisted &&
            this.isOpen()
          ) {

            this.refresh({
              openAfter: true
            });

          }

        }.bind(this)
      );

    }



    /* ========================================================
       KEYBOARD
    ======================================================== */

    handleKeydown(event) {

      if (!this.isOpen()) {
        return;
      }


      if (
        event.key === 'Escape'
      ) {

        event.preventDefault();

        this.close();

        return;

      }


      if (
        event.key !== 'Tab'
      ) {
        return;
      }


      const dialog =
        this.refs().dialog;


      if (!dialog) {
        return;
      }


      const focusables =
        Array.from(
          dialog.querySelectorAll(
            [
              'a[href]',
              'button:not([disabled])',
              'input:not([disabled])',
              'select:not([disabled])',
              'textarea:not([disabled])',
              'summary',
              '[tabindex]:not([tabindex="-1"])'
            ].join(',')
          )
        ).filter(
          function (element) {

            return (
              element.getClientRects()
                .length > 0 &&
              element.getAttribute(
                'aria-hidden'
              ) !== 'true'
            );

          }
        );


      if (!focusables.length) {

        event.preventDefault();

        dialog.focus();

        return;

      }


      const first =
        focusables[0];


      const last =
        focusables[
          focusables.length - 1
        ];


      if (
        event.shiftKey &&
        document.activeElement === first
      ) {

        event.preventDefault();

        last.focus();

      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {

        event.preventDefault();

        first.focus();

      }

    }



    /* ========================================================
       EXTERNAL CART MUTATION
    ======================================================== */

    handleExternalMutation(event) {

      const detail =
        event.detail || {};


      const type =
        detail.type;


      const payload =
        detail.payload || {};



      /* CAPTURE MOST RECENTLY ADDED PRODUCT */

      if (type === 'add') {

        const items =
          Array.isArray(payload.items)
            ? payload.items
            : [payload];


        const added =
          items[0];


        if (
          added &&
          added.product_id
        ) {

          this.lastAddedProductId =
            String(
              added.product_id
            );

        }

      }


      window.clearTimeout(
        this.externalRefreshTimer
      );


      this.externalRefreshTimer =
        window.setTimeout(
          function () {

            /*
              If the existing theme already requested
              this drawer section, use that HTML.
            */

            if (
              payload.sections &&
              payload.sections[
                this.sectionId
              ]
            ) {

              this.replaceFromHTML(
                payload.sections[
                  this.sectionId
                ],
                {
                  keepOpen:
                    this.isOpen() ||
                    (
                      type === 'add' &&
                      this.config
                        .autoOpenAfterAdd
                    ),

                  openAfter:
                    type === 'add' &&
                    this.config
                      .autoOpenAfterAdd
                }
              );


//              this.updateCountsFromCart();

this.updateCountsFromCart()
  .then(
    function (cart) {

      if (
        this.isOpen() &&
        cart &&
        cart.item_count > 0 &&
        this.config.upsells &&
        this.config.upsells.enabled
      ) {

        this.loadUpsells(
          cart
        );

      }

    }.bind(this)
  );




              return;

            }


            this.refresh({
              openAfter:
                type === 'add' &&
                this.config
                  .autoOpenAfterAdd
            });

          }.bind(this),
          80
        );

    }



    /* ========================================================
       CART MUTATION QUEUE
    ======================================================== */

    queueMutation(callback) {

      this.mutationQueue =
        this.mutationQueue
          .catch(function () {})
          .then(callback);


      return this.mutationQueue;

    }



    /* ========================================================
       CHANGE CART LINE
    ======================================================== */

    changeLine(
      line,
      quantity
    ) {

      if (!line) {
        return;
      }


      const key =
        line.dataset.lineKey;


      if (!key) {
        return;
      }


      quantity =
        Math.max(
          0,
          parseInt(
            quantity,
            10
          ) || 0
        );


      line.classList.add(
        'is-loading'
      );


      this.queueMutation(
        async function () {

          try {

            const response =
              await NativeFetch(
                endpoint(
                  'cart/change.js'
                ),
                {
                  method: 'POST',

                  headers: {
                    'Content-Type':
                      'application/json',

                    Accept:
                      'application/json'
                  },

                  body:
                    JSON.stringify({
                      id: key,

                      quantity:
                        quantity,

                      sections: [
                        this.sectionId
                      ],

                      sections_url:
                        (
 shopRoot()
                        )
                    })
                }
              );


            let cart = {};


            try {

              cart =
                await response.json();

            } catch (error) {}


            if (!response.ok) {

              throw new Error(
                cart.description ||
                cart.message ||
                'Unable to update the cart.'
              );

            }


            await this.handleCartResponse(
              cart,
              {
                keepOpen: true
              }
            );


            if (quantity === 0) {

              this.toast(
                'Item removed.'
              );

            }


          } catch (error) {

            line.classList.remove(
              'is-loading'
            );


            this.toast(
              error.message ||
              'Unable to update the cart.',
              true
            );

          }

        }.bind(this)
      );

    }



    /* ========================================================
       HANDLE CART RESPONSE
    ======================================================== */

    /* ========================================================
   HANDLE CART MUTATION RESPONSE
======================================================== */

async handleCartResponse(
  responseData,
  options = {}
) {

  const keepOpen =
    options.keepOpen !== false;



  /* ------------------------------------------------------
     REPLACE DRAWER FROM BUNDLED SECTION
  ------------------------------------------------------ */

  if (
    responseData &&
    responseData.sections &&
    responseData.sections[
      this.sectionId
    ]
  ) {

    this.replaceFromHTML(
      responseData.sections[
        this.sectionId
      ],
      {
        keepOpen:
          keepOpen
      }
    );


  } else {

    await this.refresh({
      openAfter:
        keepOpen
    });

  }



  /*
    Always ask Shopify for the canonical full cart.

    This keeps:
    - item count
    - totals
    - upsell exclusions
    - recommendation ranking

    synchronized regardless of which cart endpoint generated
    the mutation response.
  */

  let cart = null;


  try {

    cart =
      await getCart();


    this.updateCartCount(
      cart
    );


  } catch (error) {

    console.warn(
      '[Livvewell Cart] Could not fetch full cart after mutation.',
      error
    );

  }



  /* ------------------------------------------------------
     SMART UPSELLS
  ------------------------------------------------------ */

  if (
    keepOpen &&
    cart &&
    cart.item_count > 0 &&
    this.config.upsells &&
    this.config.upsells.enabled
  ) {

    this.loadUpsells(
      cart
    );

  }


  return cart;

}



    /* ========================================================
       REFRESH DRAWER VIA SECTION RENDERING
    ======================================================== */
/* ========================================================
   REFRESH DRAWER VIA SECTION RENDERING

   Always render our global static drawer against Shopify's
   locale-aware storefront root.

   This makes the drawer independent of whether the customer
   is currently on:
   - homepage
   - product
   - collection
   - page
   - article
   - policy
   - search
======================================================== */

async refresh(options = {}) {

  try {

    const renderUrl =
      shopRoot() +
      '?sections=' +
      encodeURIComponent(
        this.sectionId
      );


    const response =
      await NativeFetch(
        renderUrl,
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );


    if (!response.ok) {

      throw new Error(
        'Unable to render cart drawer section.'
      );

    }


    const sections =
      await response.json();


    const html =
      sections[
        this.sectionId
      ];


    if (!html) {

      console.warn(
        '[Livvewell Cart] Section response:',
        sections
      );


      throw new Error(
        'Cart drawer section was not returned.'
      );

    }


    const shouldOpen =
      this.isOpen() ||
      options.openAfter;


    this.replaceFromHTML(
      html,
      {
        keepOpen:
          this.isOpen()
      }
    );

/*
    const cart =
      await getCart();


    this.updateCartCount(
      cart
    );


    if (
      options.openAfter &&
      !this.isOpen()
    ) {

      this.open({
        trigger:
          options.trigger
      });

    }


    return cart;
*/


const cart =
  await getCart();


this.updateCartCount(
  cart
);


/*
  If the drawer needs opening, open it first.

  open() already loads upsells, so we don't
  need to trigger them twice.
*/

if (
  options.openAfter &&
  !this.isOpen()
) {

  this.open({
    trigger:
      options.trigger
  });

} else if (
  this.isOpen() &&
  cart &&
  cart.item_count > 0 &&
  this.config.upsells &&
  this.config.upsells.enabled
) {

  /*
    Drawer HTML was replaced while already open.

    The freshly rendered section contains an empty /
    hidden upsell area, so regenerate recommendations
    against the FINAL cart state.
  */

  this.loadUpsells(
    cart
  );

}


return cart;





  } catch (error) {

    console.error(
      '[Livvewell Cart] Refresh failed:',
      error
    );


    this.toast(
      'Unable to refresh the cart.',
      true
    );


    return null;

  }

}



    /* ========================================================
       REPLACE DRAWER HTML
    ======================================================== */

    replaceFromHTML(
      html,
      options = {}
    ) {

      if (!html) {
        return;
      }


      const wasOpen =
        options.keepOpen ||
        this.isOpen();


      const oldRefs =
        this.refs();


      const scrollTop =
        oldRefs.scroll
          ? oldRefs.scroll.scrollTop
          : 0;


      this.stopSaleTimer();


      const holder =
        document.createElement(
          'div'
        );


      holder.innerHTML =
        html;


      const fresh =
        holder.querySelector(
          ROOT_SELECTOR
        );


      if (!fresh) {

        console.warn(
          '[Livvewell Cart] Updated section did not contain drawer markup.'
        );

        return;

      }


      if (wasOpen) {

        fresh.classList.add(
          'is-open'
        );


        fresh.setAttribute(
          'aria-hidden',
          'false'
        );

      }


      this.root.replaceWith(
        fresh
      );


      this.root =
        fresh;


      this.parseConfig();

      this.bindRoot();

      this.startSaleTimer();

      this.updateRewardThreshold();


      if (wasOpen) {

        /*
          Page should already be locked if this was
          an open drawer. If not, lock it now.
        */

        if (!this.bodyStyle) {

          this.lockPage();

        }


        const newScroll =
          this.refs().scroll;


        if (newScroll) {

          newScroll.scrollTop =
            scrollTop;

        }

      }


      if (
        options.openAfter &&
        !this.isOpen()
      ) {

        this.open({
          trigger:
            options.trigger
        });

      }

    }



    /* ========================================================
       UPDATE CART COUNTS
    ======================================================== */

    updateCartCount(cart) {

      if (
        !cart ||
        typeof cart.item_count ===
          'undefined'
      ) {
        return;
      }


      const count =
        cart.item_count;


      document
        .querySelectorAll(
          [
            '[data-cart-count]',
            '[data-cart-item-count]',
            '[data-lw-global-cart-count]'
          ].join(',')
        )
        .forEach(
          function (element) {

            element.textContent =
              count;

          }
        );


      document.dispatchEvent(
        new CustomEvent(
          'lw:cart:updated',
          {
            detail: {
              cart: cart
            }
          }
        )
      );

    }



    /* ========================================================
       GET CART + UPDATE COUNTS
    ======================================================== */

    async updateCountsFromCart() {

      try {

        const cart =
          await getCart();


        this.updateCartCount(
          cart
        );


        return cart;

      } catch (error) {

        return null;

      }

    }



    /* ========================================================
       SALE TIMER
    ======================================================== */

    startSaleTimer() {

      this.stopSaleTimer();


      const timer =
        this.root.querySelector(
          '[data-lw-sale-timer]'
        );


      if (!timer) {
        return;
      }


      const endValue =
        timer.dataset.end;


      if (!endValue) {
        return;
      }


      const endTime =
        new Date(
          endValue
        ).getTime();


      if (
        !Number.isFinite(endTime)
      ) {

        console.warn(
          '[Livvewell Cart] Invalid countdown date:',
          endValue
        );

        return;

      }


      const update =
        function () {

          const remaining =
            endTime -
            Date.now();


          if (
            remaining <= 0
          ) {

            this.handleTimerExpired(
              timer
            );

            this.stopSaleTimer();

            return;

          }


          const totalSeconds =
            Math.floor(
              remaining / 1000
            );


          const hours =
            Math.floor(
              totalSeconds / 3600
            );


          const minutes =
            Math.floor(
              (
                totalSeconds % 3600
              ) / 60
            );


          const seconds =
            totalSeconds % 60;


          const hoursEl =
            timer.querySelector(
              '[data-lw-timer-hours]'
            );


          const minutesEl =
            timer.querySelector(
              '[data-lw-timer-minutes]'
            );


          const secondsEl =
            timer.querySelector(
              '[data-lw-timer-seconds]'
            );


          if (hoursEl) {

            hoursEl.textContent =
              String(hours)
                .padStart(
                  2,
                  '0'
                );

          }


          if (minutesEl) {

            minutesEl.textContent =
              String(minutes)
                .padStart(
                  2,
                  '0'
                );

          }


          if (secondsEl) {

            secondsEl.textContent =
              String(seconds)
                .padStart(
                  2,
                  '0'
                );

          }

        }.bind(this);


      update();


      this.timerInterval =
        window.setInterval(
          update,
          1000
        );

    }



    /* ========================================================
       STOP TIMER
    ======================================================== */

    stopSaleTimer() {

      if (
        this.timerInterval
      ) {

        window.clearInterval(
          this.timerInterval
        );


        this.timerInterval =
          null;

      }

    }



    /* ========================================================
       TIMER EXPIRED
    ======================================================== */

    handleTimerExpired(timer) {

      const promo =
        timer.closest(
          '[data-lw-promo]'
        );


      const behavior =
        timer.dataset.expiredBehavior ||
        'hide';


      if (!promo) {
        return;
      }


      if (
        behavior === 'message'
      ) {

        const copy =
          promo.querySelector(
            '.lw-cd-promo__timer-copy strong'
          );


        if (copy) {

          copy.textContent =
            this.config
              .timerExpiredText ||
            'This offer has ended.';

        }


        timer.hidden =
          true;

      } else {

        promo.hidden =
          true;

      }

    }



    /* ========================================================
       COPY TEXT
    ======================================================== */

    async copyText(text) {

      if (
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {

        try {

          await navigator.clipboard
            .writeText(text);

          return;

        } catch (error) {}

      }


      const textarea =
        document.createElement(
          'textarea'
        );


      textarea.value =
        text;


      textarea.setAttribute(
        'readonly',
        ''
      );


      textarea.style.position =
        'fixed';


      textarea.style.opacity =
        '0';


      document.body.appendChild(
        textarea
      );


      textarea.select();


      try {

        document.execCommand(
          'copy'
        );

      } catch (error) {}


      textarea.remove();

    }



    /* ========================================================
       EXISTING DISCOUNT CODES
    ======================================================== */

    getExistingCodes(cart) {

      const applications =
        [];


      if (
        Array.isArray(
          cart.discount_applications
        )
      ) {

        applications.push.apply(
          applications,
          cart.discount_applications
        );

      }


      if (
        Array.isArray(
          cart.cart_level_discount_applications
        )
      ) {

        applications.push.apply(
          applications,
          cart.cart_level_discount_applications
        );

      }


      const codes =
        [];


      applications.forEach(
        function (application) {

          if (
            application &&
            application.type ===
              'discount_code' &&
            application.title
          ) {

            const value =
              String(
                application.title
              ).trim();


            if (
              value &&
              !codes.some(
                function (existing) {

                  return (
                    existing.toLowerCase() ===
                    value.toLowerCase()
                  );

                }
              )
            ) {

              codes.push(
                value
              );

            }

          }

        }
      );


      return codes;

    }



    /* ========================================================
       APPLY DISCOUNT
    ======================================================== */

    async applyDiscount() {

      const input =
        this.root.querySelector(
          '[data-lw-discount-input]'
        );


      const button =
        this.root.querySelector(
          '[data-lw-discount-apply]'
        );


      const message =
        this.root.querySelector(
          '[data-lw-discount-message]'
        );


      if (
        !input ||
        !button
      ) {
        return;
      }


      const code =
        input.value.trim();


      if (!code) {

        this.setDiscountMessage(
          'Enter a discount code.',
          true
        );

        return;

      }


      button.disabled =
        true;


      this.setBusy(
        true
      );


      try {

        const currentCart =
          await getCart();


        const codes =
          this.getExistingCodes(
            currentCart
          );


        if (
          !codes.some(
            function (existing) {

              return (
                existing.toLowerCase() ===
                code.toLowerCase()
              );

            }
          )
        ) {

          codes.push(
            code
          );

        }


        const response =
          await NativeFetch(
            endpoint(
              'cart/update.js'
            ),
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json'
              },

              body:
                JSON.stringify({
                  discount:
                    codes.join(','),

                  sections: [
                    this.sectionId
                  ],

                  sections_url:
                    (
                     shopRoot()     
                    )
                })
            }
          );


        let cart = {};


        try {

          cart =
            await response.json();

        } catch (error) {}


        if (!response.ok) {

          throw new Error(
            cart.description ||
            cart.message ||
            'Unable to apply this discount.'
          );

        }


        const appliedCodes =
          this.getExistingCodes(
            cart
          );


        const applied =
          appliedCodes.some(
            function (existing) {

              return (
                existing.toLowerCase() ===
                code.toLowerCase()
              );

            }
          );


        await this.handleCartResponse(
          cart,
          {
            keepOpen: true
          }
        );


        if (applied) {

          const newDiscount =
            this.root.querySelector(
              '[data-lw-discount]'
            );


          if (newDiscount) {
            newDiscount.open = true;
          }


          this.setDiscountMessage(
            'Discount applied.',
            false
          );


          this.toast(
            'Discount applied.'
          );

        } else {

          const newDiscount =
            this.root.querySelector(
              '[data-lw-discount]'
            );


          const newInput =
            this.root.querySelector(
              '[data-lw-discount-input]'
            );


          if (newDiscount) {
            newDiscount.open = true;
          }


          if (newInput) {
            newInput.value = code;
          }


          this.setDiscountMessage(
            'That code could not be applied to this cart.',
            true
          );

        }


      } catch (error) {

        this.setDiscountMessage(
          error.message ||
          'Unable to apply this discount.',
          true
        );


      } finally {

        this.setBusy(
          false
        );


        const currentButton =
          this.root.querySelector(
            '[data-lw-discount-apply]'
          );


        if (currentButton) {

          currentButton.disabled =
            false;

        }

      }

    }



    /* ========================================================
       DISCOUNT MESSAGE
    ======================================================== */

    setDiscountMessage(
      text,
      error
    ) {

      const message =
        this.root.querySelector(
          '[data-lw-discount-message]'
        );


      if (!message) {
        return;
      }


      message.textContent =
        text || '';


      message.classList.toggle(
        'is-error',
        !!error
      );


      message.classList.toggle(
        'is-success',
        !!text && !error
      );

    }





/* ========================================================
   PRODUCT CARD AJAX ADD TO CART
======================================================== */

async submitProductCard(
  form,
  button
) {

  if (!form) {
    return;
  }


  /*
    Prevent accidental double-click adds.
  */

  if (
    form.dataset.lwAdding ===
    'true'
  ) {
    return;
  }


  form.dataset.lwAdding =
    'true';


  let oldButtonHTML = '';


  if (button) {

    oldButtonHTML =
      button.innerHTML;


    button.disabled =
      true;


    button.setAttribute(
      'aria-busy',
      'true'
    );


    button.textContent =
      'Adding…';

  }


  try {

    /*
      Use the actual Shopify product form.

      This preserves fields such as:
      - id
      - quantity
      - properties
      - selling_plan

      if we add them to product cards later.
    */

    const formData =
      new FormData(form);



    /*
      Ask Shopify to return the freshly rendered
      Livvewell cart drawer in the SAME request.
    */

    formData.set(
      'sections',
      this.sectionId
    );


    formData.set(
      'sections_url',
      shopRoot()
    );



    const response =
      await NativeFetch(
        endpoint(
          'cart/add.js'
        ),
        {
          method: 'POST',

          headers: {

            Accept:
              'application/json',

            'X-Requested-With':
              'XMLHttpRequest'

          },

          body:
            formData

        }
      );


    let result = {};


    try {

      result =
        await response.json();

    } catch (error) {}


    if (!response.ok) {

      throw new Error(
        result.description ||
        result.message ||
        'Unable to add this item to your cart.'
      );

    }



    /* ------------------------------------------------------
       REMEMBER PRODUCT FOR SMART UPSELLS
    ------------------------------------------------------ */

    const addedItem =
      Array.isArray(
        result.items
      )
        ? result.items[0]
        : result;


    if (
      addedItem &&
      addedItem.product_id
    ) {

      this.lastAddedProductId =
        String(
          addedItem.product_id
        );

    }



    /* ------------------------------------------------------
       GET ACTUAL FULL CART

       /cart/add.js is an add response, not necessarily the
       same shape as /cart.js.

       We want the complete cart for:
       - header count
       - smart recommendations
       - totals
       - product exclusion
    ------------------------------------------------------ */

    const cart =
      await getCart();



    /* ------------------------------------------------------
       USE SHOPIFY'S BUNDLED DRAWER HTML
    ------------------------------------------------------ */

    if (
      result.sections &&
      result.sections[
        this.sectionId
      ]
    ) {

      /*
        Keep its previous state for replacement, then
        explicitly open it below.
      */

      this.replaceFromHTML(
        result.sections[
          this.sectionId
        ],
        {
          keepOpen:
            this.isOpen()
        }
      );

    } else {

      /*
        Very defensive fallback.
      */

      await this.refresh();

    }



    /* ------------------------------------------------------
       UPDATE HEADER COUNT
    ------------------------------------------------------ */

    this.updateCartCount(
      cart
    );



    /* ------------------------------------------------------
       OPEN NEW DRAWER
    ------------------------------------------------------ */

    if (!this.isOpen()) {

      this.open({
        trigger:
          button
      });

    }



    /* ------------------------------------------------------
       REFRESH SMART RECOMMENDATIONS
    ------------------------------------------------------ */

    if (
      this.config.upsells &&
      this.config.upsells.enabled
    ) {

      this.loadUpsells(
        cart
      );

    }



    this.toast(
      'Added to your cart.'
    );



    document.dispatchEvent(
      new CustomEvent(
        'lw:product-card:added',
        {
          detail: {
            cart:
              cart,

            item:
              addedItem || null
          }
        }
      )
    );


  } catch (error) {

    console.error(
      '[Livvewell Cart] Product card add failed:',
      error
    );


    this.toast(
      error.message ||
      'Unable to add this item to your cart.',
      true
    );


  } finally {

    form.dataset.lwAdding =
      'false';


    /*
      The product card itself doesn't get replaced when
      the drawer is updated, so restore its button.
    */

    if (
      button &&
      document.contains(button)
    ) {

      button.disabled =
        false;


      button.removeAttribute(
        'aria-busy'
      );


      button.innerHTML =
        oldButtonHTML;

    }

  }

}



    /* ========================================================
       SMART RECOMMENDATION ANCHORS
    ======================================================== */

    getRecommendationAnchors(cart) {

      if (
        !cart ||
        !Array.isArray(cart.items)
      ) {
        return [];
      }


      const products =
        new Map();


      cart.items.forEach(
        function (item) {

          const id =
            String(
              item.product_id
            );


          if (!products.has(id)) {

            products.set(
              id,
              {
                id:
                  id,

                value:
                  0,

                handle:
                  item.handle || ''
              }
            );

          }


          const product =
            products.get(id);


          product.value +=
            toNumber(
              item.final_line_price ||
              item.line_price
            );

        }
      );


      let anchors =
        Array.from(
          products.values()
        );


      anchors.sort(
        function (a, b) {

          return (
            b.value -
            a.value
          );

        }
      );


      const strategy =
        this.config.upsells
          ? this.config.upsells.strategy
          : 'smart';



      /* LAST ADDED FIRST */

      if (
        this.lastAddedProductId &&
        (
          strategy === 'smart' ||
          strategy === 'last_added'
        )
      ) {

        anchors.sort(
          function (a, b) {

            const aLast =
              a.id ===
              String(
                this.lastAddedProductId
              );


            const bLast =
              b.id ===
              String(
                this.lastAddedProductId
              );


            if (
              aLast &&
              !bLast
            ) {
              return -1;
            }


            if (
              bLast &&
              !aLast
            ) {
              return 1;
            }


            return (
              b.value -
              a.value
            );

          }.bind(this)
        );

      }


      return anchors.slice(
        0,
        3
      );

    }



    /* ========================================================
       FETCH SHOPIFY RECOMMENDATIONS
    ======================================================== */

    async fetchRecommendations(
      productId,
      intent
    ) {

      const key =
        String(productId) +
        ':' +
        String(intent);


      if (
        recommendationCache.has(key)
      ) {

        return recommendationCache
          .get(key);

      }


      const promise =
        NativeFetch(
          endpoint(
            'recommendations/products.json'
          ) +
          '?product_id=' +
          encodeURIComponent(
            productId
          ) +
          '&limit=10' +
          '&intent=' +
          encodeURIComponent(
            intent
          ),
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        )
          .then(
            function (response) {

              if (!response.ok) {

                return {
                  products: []
                };

              }


              return response.json();

            }
          )
          .then(
            function (result) {

              return (
                result &&
                Array.isArray(
                  result.products
                )
              )
                ? result.products
                : [];

            }
          )
          .catch(
            function () {
              return [];
            }
          );


      recommendationCache.set(
        key,
        promise
      );


      return promise;

    }



    /* ========================================================
       FETCH FULL PRODUCT DATA
    ======================================================== */

    async fetchProduct(handle) {

      if (!handle) {
        return null;
      }


      if (
        productCache.has(handle)
      ) {

        return productCache
          .get(handle);

      }


      const promise =
        NativeFetch(
          endpoint(
            'products/' +
            encodeURIComponent(
              handle
            ) +
            '.js'
          ),
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        )
          .then(
            function (response) {

              if (!response.ok) {
                return null;
              }


              return response.json();

            }
          )
          .catch(
            function () {
              return null;
            }
          );


      productCache.set(
        handle,
        promise
      );


      return promise;

    }



    /* ========================================================
       BUILD SMART CANDIDATE POOL
    ======================================================== */

    async getUpsellCandidates(cart) {

      const maximum =
        Math.max(
          1,
          toNumber(
            this.config.upsells &&
            this.config.upsells.max,
            2
          )
        );


      const anchors =
        this.getRecommendationAnchors(
          cart
        );


      const cartProducts =
        new Set(
          cart.items.map(
            function (item) {

              return String(
                item.product_id
              );

            }
          )
        );


      const pool =
        new Map();



      /* ------------------------------------------------------
         ADD CANDIDATE
      ------------------------------------------------------ */

      const addCandidate =
        function (
          product,
          source,
          anchorIndex
        ) {

          if (!product) {
            return;
          }


          const id =
            String(
              product.id || ''
            );


          if (
            !id ||
            cartProducts.has(id)
          ) {
            return;
          }


          if (
            product.available === false
          ) {
            return;
          }


          const existing =
            pool.get(id);


          const candidate = {
            id:
              id,

            title:
              product.title || '',

            handle:
              product.handle ||
              handleFromUrl(
                product.url
              ),

            url:
              product.url || '',

            image:
              getProductImage(
                product
              ),

            price:
              toNumber(
                product.price
              ),

            compareAt:
              toNumber(
                product.compare_at_price
              ),

            source:
              source,

            anchorIndex:
              anchorIndex,

            original:
              product
          };


          candidate.score =
            this.scoreCandidate(
              candidate,
              cart
            );


          if (
            !existing ||
            candidate.score >
            existing.score
          ) {

            pool.set(
              id,
              candidate
            );

          }

        }.bind(this);



      /* ------------------------------------------------------
         COMPLEMENTARY
      ------------------------------------------------------ */

      for (
        let index = 0;
        index < anchors.length;
        index++
      ) {

        const recommendations =
          await this.fetchRecommendations(
            anchors[index].id,
            'complementary'
          );


        recommendations.forEach(
          function (product) {

            addCandidate(
              product,
              'complementary',
              index
            );

          }
        );

      }



      /* ------------------------------------------------------
         RELATED FALLBACK
      ------------------------------------------------------ */

      if (
        pool.size <
        maximum * 3
      ) {

        for (
          let index = 0;
          index < anchors.length;
          index++
        ) {

          const recommendations =
            await this.fetchRecommendations(
              anchors[index].id,
              'related'
            );


          recommendations.forEach(
            function (product) {

              addCandidate(
                product,
                'related',
                index
              );

            }
          );

        }

      }



      /* ------------------------------------------------------
         MERCHANT FALLBACK COLLECTION
      ------------------------------------------------------ */

      if (
        pool.size <
        maximum * 2 &&
        Array.isArray(
          this.config.fallbackProducts
        )
      ) {

        this.config
          .fallbackProducts
          .forEach(
            function (product) {

              addCandidate(
                product,
                'fallback',
                10
              );

            }
          );

      }



      /* ------------------------------------------------------
         INITIAL RANK
      ------------------------------------------------------ */

      let candidates =
        Array.from(
          pool.values()
        )
          .sort(
            function (a, b) {

              return (
                b.score -
                a.score
              );

            }
          )
          .slice(
            0,
            Math.max(
              maximum * 4,
              6
            )
          );



      /* ------------------------------------------------------
         FETCH FULL PRODUCT DETAILS
      ------------------------------------------------------ */

      candidates =
        await Promise.all(
          candidates.map(
            async function (candidate) {

              const handle =
                candidate.handle ||
                handleFromUrl(
                  candidate.url
                );


              const fullProduct =
                await this.fetchProduct(
                  handle
                );


              if (!fullProduct) {
                return null;
              }


              candidate.product =
                fullProduct;


              /*
                Preserve Shopify's tracked
                recommendation URL.
              */

              candidate.trackingUrl =
                candidate.url ||
                fullProduct.url ||
                (
                  shopRoot() +
                  'products/' +
                  fullProduct.handle
                );


              candidate.score +=
                this.scoreProductComplexity(
                  fullProduct
                );


              return candidate;

            }.bind(this)
          )
        );


      candidates =
        candidates
          .filter(Boolean)
          .filter(
            function (candidate) {

              const product =
                candidate.product;


              return (
                product &&
                product.available !== false &&
                Array.isArray(
                  product.variants
                ) &&
                product.variants.some(
                  function (variant) {
                    return variant.available;
                  }
                )
              );

            }
          )
          .sort(
            function (a, b) {

              return (
                b.score -
                a.score
              );

            }
          );


      return candidates.slice(
        0,
        maximum
      );

    }



    /* ========================================================
       CANDIDATE SCORE
    ======================================================== */

    scoreCandidate(
      candidate,
      cart
    ) {

      let score = 0;


      if (
        candidate.source ===
        'complementary'
      ) {

        score += 160;

      } else if (
        candidate.source ===
        'related'
      ) {

        score += 75;

      } else {

        score += 25;

      }


      score -=
        candidate.anchorIndex * 14;


      const cartValue =
        Math.max(
          toNumber(
            cart.items_subtotal_price
          ),
          toNumber(
            cart.total_price
          )
        );


      const price =
        toNumber(
          candidate.price
        );


      const ceiling =
        toNumber(
          this.config.upsells &&
          this.config.upsells
            .priceCeilingPercent,
          60
        );


      if (
        cartValue > 0 &&
        price > 0
      ) {

        const ratio =
          (
            price /
            cartValue
          ) * 100;


        if (
          ratio <= ceiling
        ) {

          score += 30;

        } else if (
          ratio <= 100
        ) {

          score += 7;

        } else {

          score -= 25;

        }

      }


      return score;

    }



    /* ========================================================
       PRODUCT COMPLEXITY SCORE
    ======================================================== */

    scoreProductComplexity(product) {

      const variants =
        Array.isArray(
          product.variants
        )
          ? product.variants.filter(
              function (variant) {
                return variant.available;
              }
            )
          : [];


      const plans =
        Array.isArray(
          product.selling_plan_groups
        )
          ? product.selling_plan_groups
          : [];


      let score = 0;


      if (
        variants.length === 1 &&
        plans.length === 0
      ) {

        /*
          Easy one-click add-on.
        */

        score += 18;

      }


      if (
        product.requires_selling_plan
      ) {

        score -= 5;

      }


      return score;

    }



    /* ========================================================
       LOAD UPSELLS
    ======================================================== */

    async loadUpsells(cart) {

      if (
        !this.config.upsells ||
        !this.config.upsells.enabled
      ) {
        return;
      }


      const refs =
        this.refs();


      if (
        !refs.upsells ||
        !refs.upsellList
      ) {
        return;
      }


      const requestId =
        ++this.upsellRequestId;


      refs.upsellList.innerHTML =
        '';


      refs.upsells.hidden =
        false;


      if (refs.upsellLoading) {

        refs.upsellLoading.hidden =
          false;

      }


      try {

        cart =
          cart ||
          await getCart();


        if (
          requestId !==
          this.upsellRequestId
        ) {
          return;
        }


        if (
          !cart ||
          !cart.item_count
        ) {

          refs.upsells.hidden =
            true;

          return;

        }


        const candidates =
          await this.getUpsellCandidates(
            cart
          );


        if (
          requestId !==
          this.upsellRequestId
        ) {
          return;
        }


        if (!candidates.length) {

          refs.upsells.hidden =
            true;

          return;

        }


        this.renderUpsells(
          candidates,
          cart
        );


      } catch (error) {

        console.warn(
          '[Livvewell Cart] Upsell error:',
          error
        );


        refs.upsells.hidden =
          true;


      } finally {

        const currentLoading =
          this.refs()
            .upsellLoading;


        if (currentLoading) {

          currentLoading.hidden =
            true;

        }

      }

    }



    /* ========================================================
       UPSELL REASON
    ======================================================== */

    upsellReason(source) {

      if (
        source ===
        'complementary'
      ) {
        return 'Pairs with your cart';
      }


      if (
        source ===
        'related'
      ) {
        return 'Recommended for you';
      }


      return 'A Livvewell pick';

    }



    /* ========================================================
       RENDER UPSELLS
    ======================================================== */

    renderUpsells(
      candidates,
      cart
    ) {

      const list =
        this.refs()
          .upsellList;


      if (!list) {
        return;
      }


      this.upsellProducts
        .clear();


      list.innerHTML =
        candidates
          .map(
            function (candidate) {

              const product =
                candidate.product;


              this.upsellProducts.set(
                String(product.id),
                {
                  product:
                    product,

                  candidate:
                    candidate
                }
              );


              return this.upsellHTML(
                candidate,
                cart.currency
              );

            }.bind(this)
          )
          .join('');


      list
        .querySelectorAll(
          '[data-lw-upsell]'
        )
        .forEach(
          function (card) {

            this.updateUpsellPlanOptions(
              card
            );


            this.updateUpsellPrice(
              card
            );

          }.bind(this)
        );

    }



    /* ========================================================
       UPSELL HTML
    ======================================================== */

    upsellHTML(
      candidate,
      currency
    ) {

      const product =
        candidate.product;


      const variants =
        (
          product.variants || []
        ).filter(
          function (variant) {

            return variant.available;

          }
        );


      if (!variants.length) {
        return '';
      }


      const plans =
        Array.isArray(
          product.selling_plan_groups
        )
          ? product.selling_plan_groups
          : [];


      const requiresChoice =
        variants.length > 1 ||
        plans.length > 0 ||
        product.requires_selling_plan;


      const firstVariant =
        variants[0];


      const image =
        getProductImage(
          product
        ) ||
        candidate.image;


      const compareAt =
        toNumber(
          firstVariant.compare_at_price ||
          product.compare_at_price
        );


      const price =
        toNumber(
          firstVariant.price ||
          product.price
        );


      const url =
        candidate.trackingUrl ||
        (
          shopRoot() +
          'products/' +
          product.handle
        );


      const variantOptions =
        variants
          .map(
            function (variant) {

              return (
                '<option value="' +
                escapeAttr(
                  variant.id
                ) +
                '">' +
                escapeHTML(
                  variant.title
                ) +
                '</option>'
              );

            }
          )
          .join('');


      let actionHTML = '';


      if (!requiresChoice) {

        actionHTML =
          `
            <button
              type="button"
              class="lw-cd-upsell__add"
              data-lw-upsell-direct-add
              data-product-id="${escapeAttr(product.id)}"
              data-variant-id="${escapeAttr(firstVariant.id)}"
            >
              + Add
            </button>
          `;

      } else {

        actionHTML =
          `
            <button
              type="button"
              class="lw-cd-upsell__choose"
              data-lw-upsell-toggle
              aria-expanded="false"
            >
              Choose options
              <span>+</span>
            </button>
          `;

      }


      return `
        <article
          class="lw-cd-upsell"
          data-lw-upsell
          data-product-id="${escapeAttr(product.id)}"
        >

          <a
            href="${escapeAttr(url)}"
            class="lw-cd-upsell__media"
            aria-label="${escapeAttr(product.title)}"
          >
            ${
              image
                ? `
                  <img
                    src="${escapeAttr(image)}"
                    alt="${escapeAttr(product.title)}"
                    loading="lazy"
                    width="144"
                    height="144"
                  >
                `
                : ''
            }
          </a>


          <div class="lw-cd-upsell__copy">

            <span class="lw-cd-upsell__reason">
              ${escapeHTML(
                this.upsellReason(
                  candidate.source
                )
              )}
            </span>


            <a
              href="${escapeAttr(url)}"
              class="lw-cd-upsell__title"
            >
              ${escapeHTML(product.title)}
            </a>


            <div class="lw-cd-upsell__price">

              <strong
                data-lw-upsell-price
                class="lw-cd-upsell__price-main"
              >
                ${escapeHTML(
                  formatMoney(
                    price,
                    currency
                  )
                )}
              </strong>


              <s
                data-lw-upsell-compare
                ${
                  compareAt > price
                    ? ''
                    : 'hidden'
                }
              >
                ${
                  compareAt > price
                    ? escapeHTML(
                        formatMoney(
                          compareAt,
                          currency
                        )
                      )
                    : ''
                }
              </s>

            </div>

          </div>


          <div class="lw-cd-upsell__action">
            ${actionHTML}
          </div>


          ${
            requiresChoice
              ? `
                <div
                  class="lw-cd-upsell__options"
                  data-lw-upsell-options
                  hidden
                >

                  ${
                    variants.length > 1
                      ? `
                        <label class="lw-cd-upsell__option-group">

                          <span>
                            Choose an option
                          </span>

                          <select
                            class="lw-cd-upsell__select"
                            data-lw-upsell-variant
                          >
                            ${variantOptions}
                          </select>

                        </label>
                      `
                      : `
                        <input
                          type="hidden"
                          value="${escapeAttr(firstVariant.id)}"
                          data-lw-upsell-variant
                        >
                      `
                  }


                  

                </div>
              `
              : ''
          }

        </article>
      `;

    }



    /* ========================================================
       TOGGLE UPSELL OPTIONS
    ======================================================== */

    toggleUpsellOptions(button) {

      const card =
        button.closest(
          '[data-lw-upsell]'
        );


      if (!card) {
        return;
      }


      const options =
        card.querySelector(
          '[data-lw-upsell-options]'
        );


      if (!options) {
        return;
      }


      const open =
        options.hidden;


      options.hidden =
        !open;


      button.setAttribute(
        'aria-expanded',
        String(open)
      );


      if (open) {

        this.updateUpsellPlanOptions(
          card
        );


        this.updateUpsellPrice(
          card
        );


        const select =
          options.querySelector(
            'select'
          );


        if (select) {

          window.setTimeout(
            function () {
              select.focus();
            },
            50
          );

        }

      }

    }



    /* ========================================================
       PRODUCT DATA FOR CARD
    ======================================================== */

    getCardProduct(card) {

      if (!card) {
        return null;
      }


      const record =
        this.upsellProducts.get(
          String(
            card.dataset.productId
          )
        );


      return record
        ? record.product
        : null;

    }



    /* ========================================================
       SELECTED VARIANT
    ======================================================== */

    getSelectedVariant(
      card,
      product
    ) {

      const variantElement =
        card.querySelector(
          '[data-lw-upsell-variant]'
        );


      const variantId =
        variantElement
          ? String(
              variantElement.value
            )
          : '';


      return (
        product.variants || []
      ).find(
        function (variant) {

          return (
            String(
              variant.id
            ) ===
            variantId
          );

        }
      ) || null;

    }



    /* ========================================================
       ALL SELLING PLANS
    ======================================================== */

    getSellingPlans(product) {

      const map =
        new Map();


      (
        product.selling_plan_groups ||
        []
      ).forEach(
        function (group) {

          (
            group.selling_plans ||
            []
          ).forEach(
            function (plan) {

              map.set(
                String(plan.id),
                plan
              );

            }
          );

        }
      );


      return map;

    }



    /* ========================================================
       UPDATE PLAN OPTIONS FOR VARIANT
    ======================================================== */

    updateUpsellPlanOptions(card) {

      const product =
        this.getCardProduct(
          card
        );


      if (!product) {
        return;
      }


      const variant =
        this.getSelectedVariant(
          card,
          product
        );


      const planWrap =
        card.querySelector(
          '[data-lw-upsell-plan-wrap]'
        );


      const planSelect =
        card.querySelector(
          '[data-lw-upsell-plan]'
        );


      if (
        !planWrap ||
        !planSelect ||
        !variant
      ) {
        return;
      }


      const allocations =
        variant.selling_plan_allocations ||
        [];


      if (!allocations.length) {

        planWrap.hidden =
          true;


        planSelect.innerHTML =
          '';


        return;

      }


      const plans =
        this.getSellingPlans(
          product
        );


      const previous =
        planSelect.value;


      let html = '';


      if (
        !product.requires_selling_plan
      ) {

        html +=
          '<option value="">One-time purchase</option>';

      }


      allocations.forEach(
        function (allocation) {

          const id =
            String(
              allocation.selling_plan_id
            );


          const plan =
            plans.get(id);


          const label =
            plan
              ? plan.name
              : 'Subscription';


          html +=
            '<option value="' +
            escapeAttr(id) +
            '">' +
            escapeHTML(label) +
            '</option>';

        }
      );


      planSelect.innerHTML =
        html;


      if (
        previous &&
        Array.from(
          planSelect.options
        ).some(
          function (option) {

            return (
              option.value ===
              previous
            );

          }
        )
      ) {

        planSelect.value =
          previous;

      }


      planWrap.hidden =
        false;

    }



    /* ========================================================
       SELECTED SELLING PLAN ALLOCATION
    ======================================================== */

    getSelectedAllocation(
      card,
      variant
    ) {

      const planSelect =
        card.querySelector(
          '[data-lw-upsell-plan]'
        );


      if (
        !planSelect ||
        !planSelect.value ||
        !variant
      ) {
        return null;
      }


      const id =
        String(
          planSelect.value
        );


      return (
        variant.selling_plan_allocations ||
        []
      ).find(
        function (allocation) {

          return (
            String(
              allocation.selling_plan_id
            ) ===
            id
          );

        }
      ) || null;

    }


    /* ========================================================
       UPDATE UPSELL DISPLAY IMAGE
    ======================================================== */
updateUpsellImage(card) {

  const product =
    this.getCardProduct(card);

  if (!product) {
    return;
  }


  const variant =
    this.getSelectedVariant(
      card,
      product
    );

  if (!variant) {
    return;
  }


  const imageElement =
    card.querySelector(
      '.lw-cd-upsell__media img'
    );

  if (!imageElement) {
    return;
  }


  const image =
    getVariantImage(
      variant,
      product
    );

  if (!image) {
    return;
  }


  imageElement.src =
    image;

  imageElement.removeAttribute(
    'srcset'
  );

}

    /* ========================================================
       UPDATE UPSELL DISPLAY PRICE
    ======================================================== */

    async updateUpsellPrice(card) {

      const product =
        this.getCardProduct(
          card
        );


      if (!product) {
        return;
      }


      const variant =
        this.getSelectedVariant(
          card,
          product
        );


      if (!variant) {
        return;
      }


      const allocation =
        this.getSelectedAllocation(
          card,
          variant
        );


      const cart =
        await this.getCartQuietly();


      const currency =
        cart
          ? cart.currency
          : this.config.currency;


      let price =
        toNumber(
          variant.price
        );


      let compareAt =
        toNumber(
          variant.compare_at_price
        );


      if (allocation) {

        price =
          toNumber(
            allocation.price,
            price
          );


        compareAt =
          toNumber(
            allocation.compare_at_price,
            compareAt
          );

      }


      const priceElement =
        card.querySelector(
          '[data-lw-upsell-price]'
        );


      const compareElement =
        card.querySelector(
          '[data-lw-upsell-compare]'
        );


      if (priceElement) {

        priceElement.textContent =
          formatMoney(
            price,
            currency
          );

      }


      if (compareElement) {

        if (
          compareAt > price
        ) {

          compareElement.hidden =
            false;


          compareElement.textContent =
            formatMoney(
              compareAt,
              currency
            );

        } else {

          compareElement.hidden =
            true;


          compareElement.textContent =
            '';

        }

      }

    }



    /* ========================================================
       CART CACHE FOR UI
    ======================================================== */

    async getCartQuietly() {

      try {

        return await getCart();

      } catch (error) {

        return null;

      }

    }



    /* ========================================================
       DIRECT UPSELL ADD
    ======================================================== */

    addDirectUpsell(button) {

      const productId =
        String(
          button.dataset.productId
        );


      const variantId =
        String(
          button.dataset.variantId
        );


      this.addUpsellToCart(
        productId,
        variantId,
        null,
        button
      );

    }



    /* ========================================================
       CONFIRM SELECTED UPSELL
    ======================================================== */

    confirmUpsell(button) {

      const card =
        button.closest(
          '[data-lw-upsell]'
        );


      if (!card) {
        return;
      }


      const product =
        this.getCardProduct(
          card
        );


      if (!product) {
        return;
      }


      const variant =
        this.getSelectedVariant(
          card,
          product
        );


      if (
        !variant ||
        !variant.available
      ) {

        this.toast(
          'Please choose an available option.',
          true
        );

        return;

      }


      const plan =
        card.querySelector(
          '[data-lw-upsell-plan]'
        );


      let sellingPlan =
        null;


      if (
        plan &&
        plan.value
      ) {

        sellingPlan =
          plan.value;

      }


      if (
        product.requires_selling_plan &&
        !sellingPlan
      ) {

        this.toast(
          'Please choose a subscription option.',
          true
        );

        return;

      }


      this.addUpsellToCart(
        String(
          product.id
        ),
        String(
          variant.id
        ),
        sellingPlan,
        button
      );

    }



    /* ========================================================
       ADD UPSELL TO CART
    ======================================================== */

    async addUpsellToCart(
      productId,
      variantId,
      sellingPlan,
      button
    ) {

      if (
        !variantId ||
        !button
      ) {
        return;
      }


      button.disabled =
        true;


      const previousLabel =
        button.textContent;


      button.textContent =
        'Adding…';


      try {

        const item = {
          id:
            Number(
              variantId
            ),

          quantity: 1
        };


        if (sellingPlan) {

          item.selling_plan =
            Number(
              sellingPlan
            );

        }


        const response =
          await NativeFetch(
            endpoint(
              'cart/add.js'
            ),
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json'
              },

              body:
                JSON.stringify({
                  items: [
                    item
                  ],

                  sections: [
                    this.sectionId
                  ],

                  sections_url:
                    (
                      shopRoot()
                    )
                })
            }
          );


        let result = {};


        try {

          result =
            await response.json();

        } catch (error) {}


        if (!response.ok) {

          throw new Error(
            result.description ||
            result.message ||
            'Unable to add this product.'
          );

        }


        this.lastAddedProductId =
          String(
            productId
          );


        await this.handleCartResponse(
          result,
          {
            keepOpen: true
          }
        );


        this.toast(
          'Added to your cart.'
        );


        document.dispatchEvent(
          new CustomEvent(
            'lw:upsell:added',
            {
              detail: {
                productId:
                  productId,

                variantId:
                  variantId,

                sellingPlan:
                  sellingPlan
              }
            }
          )
        );


      } catch (error) {

        button.disabled =
          false;


        button.textContent =
          previousLabel;


        this.toast(
          error.message ||
          'Unable to add this product.',
          true
        );

      }

    }

  }



  /* ==========================================================
     INITIALIZE CONTROLLER
  ========================================================== */

  function init() {

    const root =
      document.querySelector(
        ROOT_SELECTOR
      );


    if (!root) {

      return null;

    }


    if (
      controller &&
      controller.root === root
    ) {

      return controller;

    }


    controller =
      new LivvewellDrawer(
        root
      );


    return controller;

  }



  /* ==========================================================
     INSTALL CART MUTATION OBSERVERS
  ========================================================== */

  installFetchObserver();

  installXHRObserver();



  /* ==========================================================
     INITIAL PAGE LOAD
  ========================================================== */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();

  }



  /* ==========================================================
     SHOPIFY THEME EDITOR
  ========================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {

      if (
        event.target &&
        event.target.querySelector &&
        event.target.querySelector(
          ROOT_SELECTOR
        )
      ) {

        controller =
          null;


        init();

      }

    }
  );



  /* ==========================================================
     PUBLIC API
     ----------------------------------------------------------
     You can use:
       LivvewellCartDrawer.open()
       LivvewellCartDrawer.close()
       LivvewellCartDrawer.refresh()
       LivvewellCartDrawer.getCart()
  ========================================================== */

  window.LivvewellCartDrawer = {

    __loaded: true,


    open:
      function () {

        const instance =
          init();


        if (instance) {

          instance.open();

        }

      },


    close:
      function () {

        const instance =
          init();


        if (instance) {

          instance.close();

        }

      },


    refresh:
      function () {

        const instance =
          init();


        return instance
          ? instance.refresh()
          : Promise.resolve(
              null
            );

      },


    getCart:
      function () {

        return getCart();

      },


    controller:
      function () {

        return init();

      }

  };

})();